import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "../../../../../widgets/SearchPanel";
import { fnGetDataSet } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnResizeBar from "@shared/components/SnResizeBar";
import { menuInfo } from "@shared/type/CommonType";
import { ChangeColumnConfig } from "@shared/components/GridV2/SnGridTypes";

interface Props {
  Menuprops: menuInfo;
}

const SN_PRD_WRK_REP_001_CHEM = forwardRef(({ Menuprops }: Props, ref) => {
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
  });
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const searchRef = useRef<any>(null);
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const focusRequestSeq = useRef(0);
  const today = new Date();
  const todayform = `${today.getFullYear()}${(today.getMonth() + 1)
    .toString()
    .padStart(2, "0")}${today.getDate().toString().padStart(2, "0")}`;
  const popDate = new Date();
  popDate.setDate(1);
  const firstDayOfMonth = `${popDate.getFullYear()}${(popDate.getMonth() + 1)
    .toString()
    .padStart(2, "0")}${popDate.getDate().toString().padStart(2, "0")}`;

  const { userInfo } = global();

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  const onSearch = async () => {
    try {
      const param = {
        id: "sn_prd_wrk_rep_001_chem_01",
        params: searchRef.current.getFormdata(),
      };

      const data: any = await fnGetDataSet(param);

      setDataSources({
        grid1: data[0],
        grid2: [],
      });
    } catch (error) {}
  };

  useImperativeHandle(ref, () => ({
    onSearch,
  }));

  const onFocusedRowChangedL = async (data: any) => {
    const params = {
      id: "sn_prd_wrk_rep_001_chem_02",
      params: {
        cd_firm: userInfo.cd_firm,
        no_wo: data.no_wo,
        cd_group: data.cd_group,
        no_run: data.no_run,
        no_line: data.no_line,
        no_line_step: data.no_line_step ?? data.no_line,
        fg_table: data.fg_table,
      },
    };
    const requestSeq = ++focusRequestSeq.current;
    const rtn: any = await fnGetDataSet(params);
    if (requestSeq !== focusRequestSeq.current) return;

    const detailRows = rtn[0] ?? [];

    const cdGrpEquip = String(data.cd_grp_equip ?? "").trim();
    const fgTable = String(data.fg_table ?? "")
      .trim()
      .toLowerCase();
    let filterKey = "no_line";
    if (cdGrpEquip === "200") {
      filterKey = "no_line_step";
    }
    const filterValue = data.no_line;
    const hasFilterKey = detailRows.some((row: any) =>
      Object.prototype.hasOwnProperty.call(row, filterKey)
    );
    const filteredDetailRows = detailRows.filter(
      (row: any) => String(row?.[filterKey] ?? "") === String(filterValue ?? "")
    );
    const displayRows =
      hasFilterKey || filteredDetailRows.length > 0
        ? filteredDetailRows
        : detailRows;

    let visibleColumns: string[] = [
      "nm_param",
      "dc_value_plan",
      "dc_value_run",
    ];

    if (cdGrpEquip === "200") {
      if (fgTable === "cons") {
        visibleColumns = ["no_lot", "qt_use"];
      } else if (fgTable === "recal") {
        visibleColumns = ["qt"];
      } else if (fgTable === "pack") {
        visibleColumns = ["no_lot", "qt"];
      }
    } else if (cdGrpEquip === "100" || cdGrpEquip === "300") {
      visibleColumns = [
        "nm_param",
        "dc_value_plan",
        "dc_value_run",
        "tp_grpparam",
        "yn_input",
      ];
    }

    setDataSources((prev: any) => ({
      ...prev,
      grid2: displayRows,
    }));

    const visibleConfig: ChangeColumnConfig[] = columns.grid2.map(
      (col: any) => ({
        cd_column: col.cd_column,
        nm_column: col.nm_column,
        fg_visible: visibleColumns.includes(col.cd_column) ? "Y" : "N",
      })
    );

    gridRefs.current["grid2"].current.changeColumnConfigs(visibleConfig);
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged: onFocusedRowChangedL,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
      },
    }),
    [columns, dataSources]
  );
  return (
    <div className="h-full flex flex-col">
      <div>
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex w-full h-full min-w-0 space-x-1.5">
        <SnResizeBar direction={"width"} id="panel1" orgSize="70%">
          <SnGrid
            // Pros로 받은 Menuprops
            ref={gridRefs.current["grid1"]} // Grid Name
            columnConfig={grids.grid1.columns} // 컬럼 설정 전달
            rowData={grids.grid1.dataSource} // Datasource
            events={grids.grid1.events}
            etcProps={{
              rowSelection: "multiple",
              title: "그룹레시피정보",
            }}
          />
        </SnResizeBar>
        <SnResizeBar direction={"width"} id="panel2" orgSize="29.5%">
          <SnGrid
            // Pros로 받은 Menuprops
            ref={gridRefs.current["grid2"]} // Grid Name
            columnConfig={grids.grid2.columns} // 컬럼 설정 전달
            rowData={grids.grid2.dataSource} // Datasource
            etcProps={{
              rowSelection: "multiple",
              title: "기타정보",
            }}
          />
        </SnResizeBar>
      </div>
    </div>
  );
});

export default SN_PRD_WRK_REP_001_CHEM;
